import React from 'react';
import { motion } from 'framer-motion';
import { Navigation, MapPin, Route, Map, Share2, AlertTriangle, Globe } from 'lucide-react';

const FeaturesSection = () => {
  const features = [
    {
      icon: Navigation,
      title: "Live Navigation",
      description: "Real-time journey tracking with smart alerts for interchanges and exits",
      color: "#00F5D4",
      gradient: "linear-gradient(135deg, #00F5D4, #00D4B8)"
    },
    {
      icon: Route,
      title: "Metro Route Finder",
      description: "Find optimal metro routes between any two locations via stations",
      color: "#3D5AFE",
      gradient: "linear-gradient(135deg, #3D5AFE, #5C6BC0)"
    },
    {
      icon: Map,
      title: "Route Details & Planning",
      description: "Comprehensive metro route information with journey planning",
      color: "#9D4EDD",
      gradient: "linear-gradient(135deg, #9D4EDD, #C77DFF)"
    },
    {
      icon: MapPin,
      title: "Nearby Metro Stations",
      description: "Discover metro stations near your location with real-time updates",
      color: "#FF6B35",
      gradient: "linear-gradient(135deg, #FF6B35, #FF8C42)"
    },
    {
      icon: Share2,
      title: "Live Journey Sharing",
      description: "Share your metro journey with friends and family in real-time",
      color: "#4ECDC4",
      gradient: "linear-gradient(135deg, #4ECDC4, #44A08D)"
    },
    {
      icon: AlertTriangle,
      title: "Disruption Alerts",
      description: "Stay informed with instant alerts about metro disruptions and delays",
      color: "#E74C3C",
      gradient: "linear-gradient(135deg, #E74C3C, #C0392B)"
    },
    {
      icon: Globe,
      title: "Multi-Language Support",
      description: "Regional language support for seamless metro navigation experience",
      color: "#8E44AD",
      gradient: "linear-gradient(135deg, #8E44AD, #9B59B6)"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="features" className="features-section section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Key Features</h2>
          <p className="section-subtitle">
            Discover what makes MetroRahi the ultimate metro navigation companion
          </p>
        </motion.div>

        <motion.div
          className="features-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="feature-card"
              variants={cardVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
            >
              <div className="feature-icon-container" style={{ background: feature.gradient }}>
                <feature.icon className="feature-icon" />
              </div>
              
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
              
              <div className="feature-glow" style={{ borderColor: feature.color }}></div>
            </motion.div>
          ))}
        </motion.div>

        {/* Feature Highlights Banner */}
        <motion.div
          className="features-banner"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="banner-content">
            <div className="banner-text">
              <h3>Smart Metro Navigation</h3>
              <p>Our intelligent system provides optimal metro routes with real-time updates, smart alerts, and seamless multi-language support for the best metro experience</p>
            </div>
            <div className="banner-stats">
              <div className="stat-item">
                <span className="stat-number">50ms</span>
                <span className="stat-label">Route Response</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">99.0%</span>
                <span className="stat-label">Accuracy</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Live Updates</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
